import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HotelCard from "../components/HotelCard";
import SearchBar from "../components/SearchBar";
import { Slider } from "../components/ui/slider";
import { Snowflake, Users, House, Heart, PawPrint, SealCheck, Star, Sliders, X } from "@phosphor-icons/react";
import api from "../lib/api";

const AMENITY_TOGGLES = [
  { key: "has_ac", label: "AC rooms", Icon: Snowflake },
  { key: "couple_friendly", label: "Couple friendly", Icon: Heart },
  { key: "family_friendly", label: "Family friendly", Icon: House },
  { key: "unmarried_couples_allowed", label: "Unmarried couples", Icon: Users },
  { key: "pets_allowed", label: "Pets allowed", Icon: PawPrint },
  { key: "verified_only", label: "Verified only", Icon: SealCheck },
];

const RATING_OPTIONS = [
  { value: 0, label: "Any" },
  { value: 3, label: "3+" },
  { value: 4, label: "4+" },
  { value: 4.5, label: "4.5+" },
];

const SORT_OPTIONS = [
  { value: "", label: "Recommended" },
  { value: "price_asc", label: "Price · Low to High" },
  { value: "price_desc", label: "Price · High to Low" },
  { value: "rating_desc", label: "Rating · High to Low" },
];

export default function HotelsPage() {
  const [params] = useSearchParams();
  const cityParam = params.get("city") || "";
  const q = params.get("q") || "";

  const [hotels, setHotels] = useState([]);
  const [priceRange, setPriceRange] = useState([500, 15000]);
  const [toggles, setToggles] = useState({});
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("");
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const fetchHotels = () => {
    setLoading(true);
    const searchParams = {};
    if (cityParam) searchParams.city = cityParam;
    if (q) searchParams.q = q;
    // send the toggles that are turned ON as true; skip false ones
    // (a false amenity toggle would over-filter and hide most hotels).
    for (const [k, v] of Object.entries(toggles)) {
      if (v) searchParams[k] = true;
    }
    if (minRating) searchParams.min_rating = minRating;
    if (sort) searchParams.sort = sort;
    if (priceRange[0] > 500) searchParams.min_price = priceRange[0];
    if (priceRange[1] < 15000) searchParams.max_price = priceRange[1];
    api
      .get("/hotels", { params: searchParams })
      .then(({ data }) => setHotels(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHotels(); /* eslint-disable-next-line */ }, [cityParam, q, toggles, minRating, sort, priceRange]);

  const activeCount = useMemo(() =>
    Object.values(toggles).filter(Boolean).length +
    (minRating > 0 ? 1 : 0) +
    (priceRange[0] > 500 || priceRange[1] < 15000 ? 1 : 0),
  [toggles, minRating, priceRange]);

  const clearAll = () => { setToggles({}); setMinRating(0); setSort(""); setPriceRange([500, 15000]); };

  const FiltersPanel = (
    <div className="space-y-8" data-testid="filters-panel">
      <div>
        <p className="text-xs tracking-widest uppercase text-neutral-500 mb-3">Price per night</p>
        <Slider value={priceRange} onValueChange={setPriceRange} min={500} max={15000} step={200} data-testid="price-slider" />
        <div className="flex justify-between text-sm text-midnight mt-3">
          <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
          <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
        </div>
      </div>

      <div>
        <p className="text-xs tracking-widest uppercase text-neutral-500 mb-3">Vibe &amp; amenities</p>
        <div className="grid grid-cols-1 gap-2">
          {AMENITY_TOGGLES.map(({ key, label, Icon }) => {
            const active = !!toggles[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setToggles((t) => ({ ...t, [key]: !t[key] }))}
                data-testid={`filter-${key}`}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl border text-sm transition ${
                  active ? "bg-gold/10 border-gold text-midnight" : "bg-white border-black/10 text-neutral-600 hover:border-black/20"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "duotone"} className={active ? "text-gold" : "text-neutral-500"} />
                <span className="flex-1 text-left">{label}</span>
                {active && <span className="text-[10px] tracking-widest uppercase text-gold">On</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs tracking-widest uppercase text-neutral-500 mb-3">Minimum rating</p>
        <div className="flex gap-2 flex-wrap">
          {RATING_OPTIONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setMinRating(r.value)}
              data-testid={`rating-${r.value}`}
              className={`px-3 py-1.5 rounded-full text-xs tracking-wide border flex items-center gap-1 ${
                minRating === r.value ? "bg-midnight text-bone border-midnight" : "bg-white text-neutral-600 border-black/10"
              }`}
            >
              {r.value > 0 && <Star size={12} weight="fill" className={minRating === r.value ? "text-gold" : "text-neutral-400"} />}
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {activeCount > 0 && (
        <button onClick={clearAll} data-testid="clear-filters"
          className="w-full py-2.5 text-xs tracking-widest uppercase text-neutral-500 hover:text-midnight border border-black/10 rounded-full">
          Clear all filters ({activeCount})
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
          {cityParam ? `Stays in ${cityParam}` : "All Stays"}
        </p>
        <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-8">
          {hotels.length} propert{hotels.length === 1 ? "y" : "ies"} to explore
        </h1>
        <SearchBar variant="inline" />
      </div>

      {/* Mobile filter bar */}
      <div className="flex items-center justify-between mb-6 lg:hidden">
        <button
          onClick={() => setMobileFiltersOpen(true)}
          data-testid="open-filters-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-black/10 bg-white text-sm text-midnight"
        >
          <Sliders size={16} weight="duotone" /> Filters
          {activeCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gold text-midnight text-[10px]">{activeCount}</span>}
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="sort-mobile"
          className="px-3 py-2 rounded-full border border-black/10 bg-white text-sm text-midnight"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className="hidden lg:block lg:col-span-1" data-testid="filters-sidebar">
          <div className="sticky top-28 bg-white border border-black/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-lg text-midnight">Filters</h3>
              {activeCount > 0 && <span className="text-xs text-gold">{activeCount} active</span>}
            </div>
            {FiltersPanel}
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="hidden lg:flex items-center justify-between mb-6">
            <p className="text-sm text-neutral-500">
              {hotels.length} results
            </p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-desktop"
              className="px-3 py-2 rounded-full border border-black/10 bg-white text-sm text-midnight"
            >
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-stone rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : hotels.length === 0 ? (
            <div className="text-center py-24" data-testid="no-hotels">
              <p className="font-serif text-2xl text-midnight mb-2">No stays match your filters</p>
              <p className="text-neutral-500 mb-4">Try loosening the amenities or price range.</p>
              {activeCount > 0 && (
                <button onClick={clearAll} className="px-5 py-2 rounded-full bg-midnight text-bone text-sm">Clear all filters</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 stagger">
              {hotels.map((h) => (
                <HotelCard key={h.id} hotel={h} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] bg-white rounded-t-3xl overflow-y-auto p-6 pb-24">
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-2 -mx-6 px-6 border-b border-black/5">
              <h3 className="font-serif text-xl text-midnight">Filters</h3>
              <button onClick={() => setMobileFiltersOpen(false)} data-testid="close-filters-btn"
                      className="w-9 h-9 rounded-full bg-stone flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            {FiltersPanel}
            <button onClick={() => setMobileFiltersOpen(false)}
              className="fixed left-6 right-6 bottom-6 py-3 rounded-full bg-midnight text-bone text-sm tracking-widest uppercase"
              data-testid="apply-filters-btn">
              Show {hotels.length} results
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
